import React, { useState } from 'react';

import { gql } from '@apollo/client';
import { useTranslation } from '@modernloop/i18n';
import { FCWithFragments, MoreMenuButton } from '@modernloop/shared/components';
import { formatDateTimeRange, isAfterNow } from '@modernloop/shared/datetime';
import { ReverseShadowIcon, ShadowIcon, TimeIcon, TraineeIcon, UserOptionalIcon } from '@modernloop/shared/icons';
import { Avatar, Box, MenuItem, Stack, Typography } from '@mui/material';

import {
  InterviewerRole,
  ResponseStatus,
  ScheduleContentInterviewDetailsInterviewer_InterviewFragment,
  ScheduleContentInterviewDetailsInterviewer_InterviewerFragment,
} from 'src/generated/mloop-graphql';

import AdjustInterviewerRsvp from 'src/entities/ScheduleContent/ScheduleContentInterview/AdjustInterviewerRsvp';
import getResponseStatusNode from 'src/entities/common/getResponseStatusNode';

import { useOrgDirectoryService } from 'src/hooks/directoryService';

type Fragments = {
  interview: ScheduleContentInterviewDetailsInterviewer_InterviewFragment;
  interviewer: ScheduleContentInterviewDetailsInterviewer_InterviewerFragment;
};

type Props = {
  timezone: string;
};

const ScheduleContentInterviewDetailsInterviewer: FCWithFragments<Fragments, Props> = ({
  interview,
  interviewer,
  timezone,
}) => {
  const { t } = useTranslation();
  const [showChangeRsvpDialog, setShowChangeRsvpDialog] = useState(false);
  const directoryService = useOrgDirectoryService();

  const handleChangeRsvp = () => {
    setShowChangeRsvpDialog(true);
  };

  const getTraineeIcon = () => {
    if (isAfterNow(interview.startAt) && interviewer.role !== InterviewerRole.Interviewer) {
      return <TraineeIcon tooltip={t('hiring-dashboard-schedule:schedule.content.trainee')} />;
    }
    if (interviewer.role === InterviewerRole.Shadow) {
      return <ShadowIcon color="action" tooltip={t('hiring-dashboard-schedule:schedule.content.shadow')} />;
    }
    if (interviewer.role === InterviewerRole.ReverseShadow) {
      return (
        <ReverseShadowIcon color="action" tooltip={t('hiring-dashboard-schedule:schedule.content.reverseShadow')} />
      );
    }
    return null;
  };

  return (
    <Stack
      direction="row"
      justifyContent="space-between"
      key={interviewer.interviewerId}
      data-testid="schedule-content-interview-details-interviewer"
    >
      <Stack direction="row" alignItems="center" spacing={1}>
        <Stack>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Avatar src={interviewer.interviewer?.internalProfilePictureUrl || ''}>
              {interviewer.interviewer?.fullName?.charAt(0)}
            </Avatar>
            <Typography variant="body2" data-testid="schedule-content-interview-details-interviewer-name">
              {interviewer.interviewer?.fullName}
            </Typography>
          </Stack>

          {interviewer.calendarEventNote && (
            <Stack direction="row" alignItems="center" spacing={1}>
              <Box width="20px" />
              <Typography
                data-testid="schedule-content-interview-details-interviewer-event-note"
                variant="body2"
                color="text.secondary"
              >
                {interviewer.calendarEventNote}
              </Typography>
            </Stack>
          )}

          {interviewer.proposedTime && (
            <Stack direction="row" alignItems="center" gap={1}>
              <Box width="20px" />
              <TimeIcon />
              <Typography
                data-testid="schedule-content-interview-details-interviewer-proposed-time"
                variant="body2"
                color="text.secondary"
              >
                {t('hiring-dashboard-schedule:schedule.content.proposalLabel')}{' '}
                {formatDateTimeRange({
                  start: interviewer.proposedTime.startAt,
                  end: interviewer.proposedTime.endAt,
                  timezone,
                  showTzAbbr: true,
                })}
              </Typography>
            </Stack>
          )}
        </Stack>

        {getTraineeIcon()}
        {interviewer.isOptional && (
          <UserOptionalIcon
            data-testid="schedule-content-interview-details-interviewer-optional-icon"
            tooltip={t('hiring-dashboard-schedule:schedule.content.optional')}
          />
        )}
      </Stack>

      <Stack direction="row" alignItems="center" spacing={0.5}>
        {getResponseStatusNode(interviewer.responseStatus || ResponseStatus.NeedsAction, t)}

        {directoryService?.canChangeRsvp() && (
          <MoreMenuButton dataTestId="schedule-content-interview-details-interviewer-more-menu-button">
            <MenuItem
              data-testid="schedule-content-interview-details-interviewer-change-rsvp-menu-item"
              id="change_rsvp"
              key="change_rsvp"
              onClick={handleChangeRsvp}
            >
              {t('hiring-dashboard-schedule:schedule.content.changeRsvp')}
            </MenuItem>
          </MoreMenuButton>
        )}
      </Stack>

      {showChangeRsvpDialog && (
        <AdjustInterviewerRsvp
          interview={interview}
          interviewerId={interviewer.interviewerId}
          onClose={() => setShowChangeRsvpDialog(false)}
        />
      )}
    </Stack>
  );
};

ScheduleContentInterviewDetailsInterviewer.fragments = {
  interview: gql`
    ${AdjustInterviewerRsvp.fragments.interview}
    fragment ScheduleContentInterviewDetailsInterviewer_interview on ApplicationStageInterview {
      id
      ...AdjustInterviewerRsvp_interview
    }
  `,
  interviewer: gql`
    fragment ScheduleContentInterviewDetailsInterviewer_interviewer on ApplicationStageInterviewInterviewer {
      applicationStageInterviewId
      interviewerId
      interviewer {
        id
        fullName
        internalProfilePictureUrl
      }
      responseStatus
      proposedTime {
        startAt
        endAt
      }
      calendarEventNote
      isOptional
      role
    }
  `,
};

export default ScheduleContentInterviewDetailsInterviewer;
